import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Linkedin, Instagram, Mail, ArrowUpRight, Loader2 } from "lucide-react";
import { FadeUp, Overline, MaskedLines } from "../components/Reveal";
import Seo from "../components/Seo";
import { LINKS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full bg-transparent border-0 border-b border-white/20 focus:border-brand rounded-none px-0 py-4 text-paper placeholder:text-white/30 font-body font-light outline-none transition-colors duration-300 focus:ring-0";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. Gurpreet's team will be in touch soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-40">
      <Seo
        title="Contact — Gurpreet Paul"
        description="Get in touch with Gurpreet Paul, Founder & CEO of ThirsTea USA, for franchise partnerships, speaking engagements and media inquiries. Email Contact@thirstea.com or connect on LinkedIn."
      />
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-32 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-6">
          <FadeUp>
            <Overline>Contact</Overline>
          </FadeUp>
          <MaskedLines
            lines={["Let's", "connect."]}
            className="font-display font-medium tracking-tighter leading-[0.9] text-6xl sm:text-7xl lg:text-8xl text-paper mt-6"
            delay={0.15}
          />
          <FadeUp delay={0.35}>
            <p className="mt-8 text-white/55 text-base md:text-lg leading-relaxed font-body font-light max-w-md">
              Franchise partnerships, speaking engagements, media inquiries — or simply a conversation
              about building something that lasts.
            </p>
            <div className="mt-12 flex flex-col gap-6">
              <a
                href={`mailto:${LINKS.email}`}
                data-testid="contact-email-link"
                className="group inline-flex items-center gap-4 text-paper hover:text-brand transition-colors duration-300"
              >
                <span className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center group-hover:border-brand transition-colors duration-300">
                  <Mail size={17} />
                </span>
                <span className="font-body">{LINKS.email}</span>
              </a>
              <a
                href={LINKS.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-linkedin-link"
                className="group inline-flex items-center gap-4 text-paper hover:text-brand transition-colors duration-300"
              >
                <span className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center group-hover:border-brand transition-colors duration-300">
                  <Linkedin size={17} />
                </span>
                <span className="font-body">linkedin.com/in/gurpreetpaulusa</span>
                <ArrowUpRight size={14} className="text-white/30" />
              </a>
              <a
                href={LINKS.linkedinCompany}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-linkedin-company-link"
                className="group inline-flex items-center gap-4 text-paper hover:text-brand transition-colors duration-300"
              >
                <span className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center group-hover:border-brand transition-colors duration-300">
                  <Linkedin size={17} />
                </span>
                <span className="font-body">linkedin.com/company/thirstea-usa</span>
                <ArrowUpRight size={14} className="text-white/30" />
              </a>
              <a
                href={LINKS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram-link"
                className="group inline-flex items-center gap-4 text-paper hover:text-brand transition-colors duration-300"
              >
                <span className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center group-hover:border-brand transition-colors duration-300">
                  <Instagram size={17} />
                </span>
                <span className="font-body">@thirstea_usa_</span>
                <ArrowUpRight size={14} className="text-white/30" />
              </a>
            </div>
          </FadeUp>
        </div>

        <FadeUp delay={0.25} className="lg:col-span-6">
          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="border border-white/10 bg-[#111113] p-8 md:p-12"
          >
            <p className="text-[11px] uppercase tracking-[0.25em] text-brand font-body mb-10">Send a Message</p>
            <div className="space-y-8">
              <div>
                <label htmlFor="name" className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-body">Name</label>
                <input
                  id="name"
                  name="name"
                  required
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  data-testid="contact-form-name"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="email" className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-body">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  data-testid="contact-form-email"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="subject" className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-body">Subject</label>
                <input
                  id="subject"
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  placeholder="Franchise, speaking, media…"
                  data-testid="contact-form-subject"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="message" className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-body">Message</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={4}
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell me about it…"
                  data-testid="contact-form-message"
                  className={`${inputClass} resize-none`}
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-form-submit"
                className="inline-flex items-center gap-2 rounded-full bg-brand px-8 py-4 text-[12px] uppercase tracking-[0.15em] text-white hover:bg-brand-dark transition-colors duration-300 disabled:opacity-60"
              >
                {sending ? <Loader2 size={15} className="animate-spin" /> : null}
                {sending ? "Sending…" : "Send Message"}
              </button>
            </div>
          </form>
        </FadeUp>
      </section>
    </main>
  );
}
